import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Card, Spin, message, Table, Button, Tag } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { ColumnType } from 'antd/es/table';
import { UploadResponse } from '../types';
import { smartReviewService } from '../services/smartReviewService';
import { NimbusLogo } from './NimbusLogo';
import { FileGroupTabs } from './FileGroupTabs';
import { NoDataVisualization } from './NoDataVisualization';
import './DataVisualization.css';

export const DataVisualization: React.FC = () => {
  const { flowId } = useParams<{ flowId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState<UploadResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const locationState = location.state as { data?: UploadResponse } | null;

    if (locationState?.data) {
      setData(locationState.data);
      setLoading(false);
    } else if (flowId) {
      fetchData(flowId);
    } else {
      setLoading(false);
    }
  }, [flowId, location.state]);

  const fetchData = async (id: string) => {
    setLoading(true);
    try {
      const response = await smartReviewService.getFlowData(id);
      if (response.status === 'processing') {
        // Flow still running: send the user to the polling page
        navigate(`/polling/${id}`, { replace: true });
        return;
      }
      setData(response);
    } catch (error: any) {
      // Silently handle error - will show NoDataVisualization component
    } finally {
      setLoading(false);
    }
  };

  const downloadTableAsCSV = () => {
    if (!data?.table || data.table.length === 0) {
      message.error('No table data available to download');
      return;
    }

    try {
      const headers = Object.keys(data.table[0]);
      const csvContent = [
        headers.join(','),
        ...data.table.map(row => 
          headers.map(header => {
            const value = row[header];
            const stringValue = String(value || '');
            return stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') 
              ? `"${stringValue.replace(/"/g, '""')}"` 
              : stringValue;
          }).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'table-data.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success('CSV file downloaded successfully');
    } catch (error: any) {
      message.error('Failed to download CSV file. Please try again.');
    }
  };


  const tableColumns = useMemo(() => {
    if (!data?.table || data.table.length === 0) return [];
    
    const firstRow = data.table[0];
    const allKeys = Object.keys(firstRow);
    
    // Priority columns that should come first with filters
    const priorityColumns = ['TORT', 'FIRM', 'MARKETINGSOURCE', 'VIABILITY STATUS'];
    
    // Get unique values for filter columns
    const getUniqueValues = (columnKey: string) => {
      const values = data.table
        .map(row => row[columnKey])
        .filter((value, index, arr) => 
          value !== null && value !== undefined && arr.indexOf(value) === index
        )
        .sort();
      
      return values.map(value => ({ text: String(value), value: String(value) }));
    };
    
    // Create column configuration
    const createColumn = (key: string, index: number): ColumnType<Record<string, any>> => ({
      title: (
        <div 
          style={{ 
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={key}
        >
          {key}
        </div>
      ),
      dataIndex: key,
      key: key,
      width: 120,
      ellipsis: true,
      render: (text: any) => {
        if (text === null || text === undefined || text === '') {
          return null;
        }
        return (
          <div 
            style={{ 
              maxWidth: '180px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={String(text)}
          >
            {String(text)}
          </div>
        );
      },
      sorter: (a, b) => {
        const aVal = a[key];
        const bVal = b[key];
        
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return aVal - bVal;
        }
        
        return String(aVal).localeCompare(String(bVal));
      },
      ...(priorityColumns.includes(key) && {
        filters: getUniqueValues(key),
        onFilter: (value, record) => String(record[key]) === value,
        filterSearch: true,
        fixed: 'left',
      }),
    });
    
    // Arrange columns: priority columns first, then others
    const priorityColumnsPresent = priorityColumns.filter(col => allKeys.includes(col));
    const remainingColumns = allKeys.filter(col => !priorityColumns.includes(col));
    const orderedKeys = [...priorityColumnsPresent, ...remainingColumns];
    
    return orderedKeys.map((key, index) => createColumn(key, index));
  }, [data?.table]);

  const tableDataSource = useMemo(() => {
    if (!data?.table) return [];

    return data.table.map((row, index) => ({
      ...row,
      key: index.toString(),
    }));
  }, [data?.table]);

  const firmFiles = useMemo(
    () => (data?.files || []).filter((file) => file.category === 'firm'),
    [data?.files]
  );

  const marketingFiles = useMemo(
    () => (data?.files || []).filter((file) => file.category === 'marketingsource'),
    [data?.files]
  );


  if (loading) {
    return (
      <div className="data-visualization-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!loading && !data) {
    return <NoDataVisualization />;
  }

  if (!data) {
    return null;
  }

  return (
    <div className="data-visualization-container">
      <Card 
        title={
          <div className="data-visualization-title-container">
            <NimbusLogo className="data-visualization-logo" />
          </div>
        }
        className="data-visualization-main-card"
      >
        <div className="data-visualization-content">
          {/* FIRM Section */}
          <FileGroupTabs
            title="FIRM Analysis"
            files={firmFiles}
            expectedTotal={data.total_firms ?? firmFiles.length}
          />

          {/* MARKETING Section */}
          <FileGroupTabs
            title="MARKETING SOURCE Analysis"
            files={marketingFiles}
            expectedTotal={data.total_marketing_sources ?? marketingFiles.length}
          />

          {/* Table Data Section */}
          <div className="data-visualization-section">
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Tag color="orange" style={{ margin: 0, fontSize: '12px', fontWeight: 'bold' }}>
                    {tableDataSource.length} Records
                  </Tag>
                  <span>Table Data</span>
                </div>
              }
              className="data-visualization-section-card"
              extra={
                <Button
                  icon={<DownloadOutlined />}
                  onClick={downloadTableAsCSV}
                  disabled={!data?.table || data.table.length === 0}
                >
                  Download CSV
                </Button>
              }
            >
              <Table
                columns={tableColumns}
                dataSource={tableDataSource}
                pagination={{
                  pageSize: 25,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '25', '50', '100'],
                  showQuickJumper: true,
                  showTotal: (total, range) => 
                    `${range[0]}-${range[1]} of ${total} items`,
                }}
                scroll={{ 
                  x: 'max-content',
                  scrollToFirstRowOnChange: true,
                }}
                size="middle"
                bordered
                tableLayout="fixed"
              />
            </Card>
          </div>
        </div>
      </Card>
    </div>
  );
};